import renderIcon from 'helpers/renderIcon';
import * as React from 'react';
import { IFieldProps, TreeSelectorCheckboxField } from './TreeCheckboxFieldComponent';
import { Field } from 'redux-form';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ITranslateProps } from 'models/common';

require('./style.scss');
const classNames = require('classnames');

export interface ITreeSelectorItem<T> {
	/**
	 * Used to group by category (top bar)
	 */
	categoryKey: ITreeSelectorCategory['key'];
	categoryName: ITreeSelectorCategory['label'];
	/**
	 * Used to group together into dropdowns
	 */
	dropdownCategoryKey: ITreeSelectorCategory['key'];
	dropdownCategoryName: ITreeSelectorCategory['label'];
	data: T;
}

export interface ITreeSelectorCategory {
	key: number | string;
	label: string;
}

export interface ITreeSelectorDropdownCategory extends ITreeSelectorCategory {
	categoryKey: ITreeSelectorCategory['key'];
}

export interface IOutsideFieldProps extends IFieldProps {
	name: string;
}

enum CheckedStatus {
	Unchecked = 'unchecked',
	Partially = 'partially_checked',
	Checked = 'checked',
}

interface IProps<T> extends ITranslateProps {
	items: ITreeSelectorItem<T>[];
	hideSelectAll?: boolean;
	/**
	 * noDropdown: use just titles instead of dropdowns
	 */
	noDropdown?: boolean;
	/**
	 * if you don't want to use memoisation for categories, leave this empty.
	 * if memoKey changes, categories will recalculate
	 */
	memoKey?: string;
	/**
	 * How should each item be rendered?
	 */
	renderItemLabel: (item: T) => JSX.Element;
	/**
	 * Props to be passed to the Field of each item
	 */
	itemToFieldProps: (item: T) => IOutsideFieldProps;
	/**
	 * Use this to filter items and keep categories
	 */
	filterItems?: (value: ITreeSelectorItem<T>, index: number, array: ITreeSelectorItem<T>[]) => boolean;
	/**
	 * Note to show when filterItems returns no items
	 */
	noFilterItemsErrorComponent?: JSX.Element;
	/**
	 * Get value of the item from current form values
	 */
	getItemCurrentValue: (item: T) => any;
	change: (field: string, value: any) => void;
}

interface IState<T> {
	chosenServiceCategoryKey: ITreeSelectorCategory['key'];
	hiddenDropdownCategories: Object;
	// for scroll
	categoriesNotScrolled: boolean;
	categoriesScrolledToTheEnd: boolean;
	categoriesNotScrollable: boolean;
	filteredItems: ITreeSelectorItem<T>[];
}
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class TreeSelector<T> extends React.Component<IProps<T>, IState<T>> {
	private categoriesMemo: { memoKey: string; categories: ITreeSelectorCategory[] };
	private dropdownCategoriesMemo: { memoKey: string; categories: ITreeSelectorDropdownCategory[] };
	private serviceCategoriesRef;
	private scrollTimeout;

	constructor(props: IProps<T>) {
		super(props);

		this.state = {
			chosenServiceCategoryKey: props.items?.[0]?.categoryKey,
			categoriesNotScrolled: true,
			categoriesScrolledToTheEnd: false,
			hiddenDropdownCategories: {},
			filteredItems: this.props.items,
			categoriesNotScrollable: false,
		};

		this.serviceCategoriesRef = React.createRef();

		this.handleServiceCategoriesScroll = this.handleServiceCategoriesScroll.bind(this);
		this.scrollCategories = this.scrollCategories.bind(this);
		this.scrollEvent = this.scrollEvent.bind(this);
		this.handleServiceCategorySelect = this.handleServiceCategorySelect.bind(this);
		this.toggleDropdownCategory = this.toggleDropdownCategory.bind(this);
		this.itemsCheckedStatus = this.itemsCheckedStatus.bind(this);
	}

	get items() {
		if (!this.props.filterItems || !this.state.filteredItems) return this.props.items;

		return this.state.filteredItems;
	}

	get unfilteredItems() {
		return this.props.items;
	}

	get categories(): ITreeSelectorCategory[] {
		if (this.categoriesMemo && this.categoriesMemo?.memoKey == this.props.memoKey) {
			return this.categoriesMemo.categories;
		}
		const categoriesObj = {};
		this.unfilteredItems?.forEach((item) => {
			if (!Object.prototype.hasOwnProperty.call(categoriesObj, item.categoryKey)) {
				categoriesObj[item.categoryKey] = item.categoryName;
			}
		});
		const categories = Object.keys(categoriesObj).map((key) => ({
			key,
			label: categoriesObj[key],
		}));

		this.categoriesMemo = { memoKey: this.props.memoKey, categories };
		return categories;
	}

	get dropdownCategories(): ITreeSelectorDropdownCategory[] {
		if (this.dropdownCategoriesMemo && this.dropdownCategoriesMemo?.memoKey == this.props.memoKey) {
			return this.dropdownCategoriesMemo.categories;
		}
		const categoriesObj = {};
		this.items?.forEach((item) => {
			if (!Object.prototype.hasOwnProperty.call(categoriesObj, item.categoryKey)) {
				categoriesObj[item.dropdownCategoryKey] = item;
			}
		});
		const categories = Object.keys(categoriesObj).map((key) => ({
			categoryKey: categoriesObj[key].categoryKey,
			key,
			label: categoriesObj[key].dropdownCategoryName,
		}));

		this.dropdownCategoriesMemo = { memoKey: this.props.memoKey, categories };
		return categories;
	}

	private scrollEvent() {
		clearTimeout(this.scrollTimeout);
		this.scrollTimeout = setTimeout(() => {
			this.handleServiceCategoriesScroll();
		}, 50);
	}

	public componentDidUpdate(prevProps: Readonly<IProps<T>>): void {
		if (prevProps.memoKey != this.props.memoKey) {
			if (this.props.filterItems) {
				this.setState({ filteredItems: this.unfilteredItems.filter(this.props.filterItems) });
			}
		}

		if (this.items?.findIndex((item) => item?.categoryKey == this.state.chosenServiceCategoryKey) == -1) {
			const newCat = this.items?.[0]?.categoryKey;
			if (newCat && newCat != this.state.chosenServiceCategoryKey) {
				this.setState({ chosenServiceCategoryKey: newCat });
			}
		}

		const newCatNotScroll =
			this.serviceCategoriesRef?.current?.clientWidth >= this.serviceCategoriesRef?.current?.scrollWidth;
		if (this.state.categoriesNotScrollable != newCatNotScroll) {
			this.setState({ categoriesNotScrollable: newCatNotScroll });
		}
	}

	public componentDidMount(): void {
		this.setState({
			categoriesNotScrollable:
				this.serviceCategoriesRef?.current?.clientWidth >= this.serviceCategoriesRef?.current?.scrollWidth,
		});
		this.serviceCategoriesRef.current.addEventListener('scroll', this.scrollEvent);
	}

	public componentWillUnmount() {
		if (this.serviceCategoriesRef?.current) {
			this.serviceCategoriesRef.current.removeEventListener('scroll', this.scrollEvent);
		}
	}

	private handleServiceCategoriesScroll() {
		const serviceCategoriesContainer = this.serviceCategoriesRef.current;
		if (serviceCategoriesContainer) {
			this.setState({
				categoriesNotScrolled: serviceCategoriesContainer.scrollLeft === 0,
				categoriesScrolledToTheEnd:
					Math.abs(serviceCategoriesContainer.scrollLeft) >=
					serviceCategoriesContainer.scrollWidth - serviceCategoriesContainer.clientWidth,
			});
		}
	}

	private scrollCategories(direction: 'left' | 'right') {
		const serviceCategoriesContainer = this.serviceCategoriesRef.current;

		if (serviceCategoriesContainer && direction) {
			const pxToScrollOnClick = 240;

			if (direction === 'right') {
				serviceCategoriesContainer.scroll({
					left: pxToScrollOnClick + serviceCategoriesContainer.scrollLeft,
					behavior: 'smooth',
				});
			}
			if (direction === 'left') {
				serviceCategoriesContainer.scroll({
					left: serviceCategoriesContainer.scrollLeft - pxToScrollOnClick,
					behavior: 'smooth',
				});
			}
		}
	}

	private handleServiceCategorySelect(categoryKey: ITreeSelectorCategory['key']) {
		this.setState({ chosenServiceCategoryKey: categoryKey });
	}

	private toggleDropdownCategory(dropdownCategoryKey: ITreeSelectorCategory['key']) {
		this.setState((state) => ({
			hiddenDropdownCategories: Object.assign(state.hiddenDropdownCategories, {
				[dropdownCategoryKey]: !state.hiddenDropdownCategories[dropdownCategoryKey],
			}),
		}));
	}

	private itemsCheckedStatus(items: IProps<T>['items']): CheckedStatus {
		let noneChecked = true;
		let allChecked = true;

		for (const item of items) {
			const checked = this.props.getItemCurrentValue(item.data);

			if (checked) {
				noneChecked = false;
			}

			if (!checked) {
				allChecked = false;
			}

			if (!noneChecked && !allChecked) {
				break;
			}
		}

		if (noneChecked) {
			return CheckedStatus.Unchecked;
		}
		if (allChecked) {
			return CheckedStatus.Checked;
		}
		return CheckedStatus.Partially;
	}

	get notEmptyCategories(): { [key: string]: boolean } | { [key: number]: boolean } {
		const categoryStates = {};
		this.items.forEach((item) => {
			categoryStates[item.categoryKey] = true;
		});
		return categoryStates;
	}

	public render() {
		const { renderItemLabel, itemToFieldProps, t } = this.props;
		const items = this.items;
		const notEmptyCategories = this.notEmptyCategories;
		const categoryItems = items.filter((item) => item.categoryKey == this.state.chosenServiceCategoryKey);
		const selectAllStatus = this.itemsCheckedStatus(categoryItems);

		return (
			<div className="treeSelector">
				<div className="categories">
					{!this.state.categoriesNotScrolled && (
						<div className="scrollLeft" onClick={() => this.scrollCategories('left')}>
							{renderIcon('roundLeft')}
						</div>
					)}

					<div
						className={`serviceCategories ${
							this.state.categoriesScrolledToTheEnd && this.state.categoriesNotScrolled ? 'noScroll' : ''
						}`}
						ref={this.serviceCategoriesRef}
					>
						{this.categories.map((category) => {
							return (
								<span
									key={category.key}
									className={classNames(`serviceCategory`, {
										active: this.state.chosenServiceCategoryKey == category.key,
										muted: !notEmptyCategories?.[category.key],
									})}
									onClick={() => {
										if (notEmptyCategories?.[category.key]) this.handleServiceCategorySelect(category.key);
									}}
								>
									{category.label}
								</span>
							);
						})}
					</div>

					{!(this.state.categoriesScrolledToTheEnd || this.state.categoriesNotScrollable) && (
						<div className="scrollRight" onClick={() => this.scrollCategories('right')}>
							{renderIcon('roundRight')}
						</div>
					)}
				</div>
				<div className="mainSection">
					{!!categoryItems.length && (
						<div
							className="selectAll"
							onClick={() =>
								categoryItems.forEach((item) =>
									this.props.change(itemToFieldProps(item.data).name, selectAllStatus != CheckedStatus.Checked),
								)
							}
						>
							<span className="checkbox">{renderIcon(selectAllStatus)}</span>
							<span className="selectAllText">{t('BSPortal_Common_SelectAll')}</span>
							<span className="selectAllGrayText">({categoryItems.length})</span>
						</div>
					)}
					{!!categoryItems.length &&
						this.dropdownCategories
							.filter((item) => item.categoryKey == this.state.chosenServiceCategoryKey)
							.map((dropdownCategory) => {
								const dropdownItems = categoryItems.filter((item) => item.dropdownCategoryKey == dropdownCategory.key);
								const dropdownCheckedStatus = this.itemsCheckedStatus(dropdownItems);

								return (
									<React.Fragment key={`dropdown_category_${dropdownCategory.key}`}>
										<div className="dropdownCategory">
											<h3
												className="categoryLabel"
												onClick={
													// Check the whole dropdown category
													() =>
														dropdownItems.forEach((item) =>
															this.props.change(
																itemToFieldProps(item.data).name,
																dropdownCheckedStatus != CheckedStatus.Checked,
															),
														)
												}
											>
												<span className="checkbox">{renderIcon(dropdownCheckedStatus, 25, 24)}</span>
												{dropdownCategory.label}
											</h3>
											{this.state.hiddenDropdownCategories?.[dropdownCategory.key]
												? renderIcon('leftRoundNoborder', 24, 24, 'arrowUp', () =>
														this.toggleDropdownCategory(dropdownCategory.key),
												  )
												: renderIcon('leftRoundNoborder', 24, 24, 'arrowDown', () =>
														this.toggleDropdownCategory(dropdownCategory.key),
												  )}
										</div>
										<div
											className={classNames('dropdownBody', {
												open: !this.state.hiddenDropdownCategories?.[dropdownCategory.key],
											})}
										>
											{dropdownItems.map((item, i) => (
												<Field
													key={'_item_' + item.categoryKey + '_' + item.dropdownCategoryKey + '_' + i}
													component={TreeSelectorCheckboxField}
													renderLabel={renderItemLabel}
													data={item.data}
													{...itemToFieldProps(item.data)}
												/>
											))}
										</div>
									</React.Fragment>
								);
							})}
					{!this.items?.length && this.props.noFilterItemsErrorComponent}
				</div>
			</div>
		);
	}
}

export default TreeSelector;
